<template>
  <div class="filter">
    <div class="filter-content">
      <div class="filter__title">
        <TitleBlock :text="title" :size="'small'" color="corporateblue" />
      </div>
      <el-collapse>
        <el-tree
          ref="filter"
          :data="visibleFilterData"
          show-checkbox
          node-key="id"
          :default-expanded-keys="combinedInitialSelection"
          :default-checked-keys="combinedInitialSelection"
          :props="defaultProps"
          auto-expand-parent
          @check="handleChange"
        />
      </el-collapse>
    </div>
    <div v-if="checkFilterData()" class="filter-bottom">
      <div class="filter__save">
        <button
          v-if="title.toLowerCase() !== 'trackers filters'"
          class="filter-save"
          @click="saveFilter"
        >
          Save to Watchlist
        </button>
      </div>
      <div v-if="reset" class="filter__reset">
        <button class="filter-reset" @click="resetFilter">Reset filters</button>
      </div>
    </div>
  </div>
</template>

<script>
import { escapeRegExp } from "lodash";
export default {
  name: "FilterBlock",
  components: {
    TitleBlock: () => import("../elements/TitleBlock"),
  },
  props: {
    title: {
      type: String,
      default: null,
    },

    reset: {
      type: Boolean,
      default: false,
    },

    filterData: {
      type: Array,
      default: null,
    },
    watchlistKeys: {
      type: Array,
      default: null,
    },
    initialSelection: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      defaultProps: {
        children: "children",
        label: "label",
      },
      filterChangeTimeout: 0,
      customFilterData: null,
      queryParams: "",
      licenseParams: "",
      productParams: "",
      requirementParams: "",
      checkedKeys: null,
      watchlistTriggered: false,
      visibleFilterData: [],
      expandKeys: null,
      combinedInitialSelection: [],
    };
  },
  computed: {
    keys() {
      return this.watchlistKeys;
    },
  },
  watch: {
    reset(newVal) {
      // eslint-disable-next-line vue/no-mutating-props
      this.reset = newVal;
    },
    $route() {
      this.changeFilterByQuery();
    },
    "$route.params.jurisdiction"(value) {},
  },
  destroyed() {
    this.$store.commit("Search/setData", {
      label: "searchFiltersQuery",
      data: null,
    });
  },
  created() {
    this.combinedInitialSelection = Array.from(new Set([...this.initialSelection, ...this.extractFilterIdsFromQuery()]));
  },
  mounted() {
    const data = [];

    // Find if initialSelection have authorities into it.
    for (let i = 0; i < this.filterData.length; i++) {
      const cat = this.filterData[i];
      if (cat.id === "authorities") {
        const newchildren = [];
        for (let j = 0; j < cat.children.length; j++) {
          let found = false;
          const jid = parseInt(cat.children[j].jurisdictionId);
          if (this.combinedInitialSelection.includes(jid)) {
            found = true;
          } else {
            for (let k = 0; k < cat.children[j].children.length; k++) {
              const auth = cat.children[j].children[k];
              if (this.combinedInitialSelection.includes(auth.id)) {
                found = true;
              }
            }
          }
          if (found) {
            newchildren.push(cat.children[j]);
          }
        }

        if (newchildren.length === 0) {
          data.push({
            category: "authorities",
            children: [
              {
                category: "authorities",
                checked: false,
                children: [],
                id: "empty",
                label:
                  "Please select a jurisdiction with one or more authorities available to display this section.",
                disabled: true,
              },
            ],
            checked: false,
            id: "authorities",
            label: "Authorities",
          });
        } else {
          data.push({
            category: "authorities",
            children: newchildren,
            id: "authorities",
            label: "Authorities",
          });
        }
        continue;
      }
      data.push(cat);
    }
    this.visibleFilterData = data;
    // Forcing an update as otherwise the query-based initial values won't actually update the content
    this.handleChange();
  },
  methods: {
    updateVisibleFilterData() {
      if (this.visibleFilterData.length < 2) {
        return;
      }
      if (this.visibleFilterData[1].id !== "authorities") {
        return;
      }

      // Remove place holder
      if (this.visibleFilterData[1].children[0].id === "empty") {
        this.visibleFilterData[1].children = [];
      }

      const checkedJurisdictionIds = this.getChecked();
      const checkedAuthorityNodes = this.getCheckedNodes("authorities");
      const authoritiesShown =
        this.visibleFilterData[1].children.length > 0
          ? this.visibleFilterData[1].children.map((el) => el.jurisdictionId)
          : [];

      const jurisdictionsNotInAuthoritiesShown = checkedJurisdictionIds.filter(
        (item) => !authoritiesShown.includes(item)
      );
      const authoritiesShownNotInJurisdictions =
        authoritiesShown.filter(
          (item) => !checkedJurisdictionIds.includes(item)
        ) || [];
      const authorityGroupsToKeepOpen = new Set(
        checkedAuthorityNodes.map((item) => item.jurisdictionId)
      );

      // For each selected jurisdiction not in authorities try to add if authorities exist.
      jurisdictionsNotInAuthoritiesShown.forEach((item) => {
        const jurisdictionToAdd = this.filterData[1].children.find(
          (el) => el.jurisdictionId === item
        );
        if (jurisdictionToAdd) {
          this.visibleFilterData[1].children.push(jurisdictionToAdd);
        }
      });

      // For each authority group not in a checked jurisdiction or has an existing authority checked, remove that group from authorities.
      authoritiesShownNotInJurisdictions.forEach((item) => {
        this.visibleFilterData[1].children =
          this.visibleFilterData[1].children.filter(
            (el) =>
              el.jurisdictionId !== item || authorityGroupsToKeepOpen.has(item)
          );
      });

      // If authorities length is zero, reinsert the placeholder.
      if (this.visibleFilterData[1].children.length === 0) {
        this.insertAuthoritiesPlaceholder();
      }
    },

    getChecked(category = "jurisdictions") {
      let ids = [];
      if (this.$refs.filter) {
        const checkedNodes = this.$refs.filter
          .getCheckedNodes()
          .filter((el) => el.category !== el.id && el.category === category);
        ids = checkedNodes.map((c) => {
          return c.id.toString();
        });
      }
      return ids;
    },

    getCheckedNodes(category = "jurisdictions") {
      let nodes = [];
      if (this.$refs.filter) {
        nodes = this.$refs.filter
          .getCheckedNodes()
          .filter((el) => el.category !== el.id && el.category === category);
      }
      return nodes;
    },

    insertAuthoritiesPlaceholder() {
      // Remove Authorities.
      this.visibleFilterData[1].children = [
        {
          category: "authorities",
          checked: false,
          children: [],
          id: "empty",
          label:
            "Please select a jurisdiction with one or more authorities available to display this section.",
          disabled: true,
        },
      ];
    },

    changeSelectedItems(data) {
      this.watchlistTriggered = true;
      this.$refs.filter.setCheckedKeys(data);
      this.checkedKeys = data;
      this.$refs.filter.defaultExpandedKeys = data;
      this.handleChange();
    },
    changeFilterByQuery() {
      if (!this.$route.query) return;

      const query = this.$route.query;
      this.$store.commit("Search/setData", {
        label: "searchFiltersQuery",
        data: query,
      });

      const filters = Object.keys(query)
        .filter(
          (q) =>
            q !== "query" &&
            q !== "page" &&
            q !== "sort_by" &&
            q !== "sort_order"
        )
        .map((q) => query[q]);

      const filtersIds = [].concat(...filters);

      this.resetFilter();

      this.checkedKeys = filtersIds;
      // this.expandKeys = filtersIds.map((f) => {
      //   const ref = this.$refs.filter.getNode(f);
      //   if (ref) return ref.parent.data.id;
      // });
    },
    extractFilterIdsFromQuery() {
      if(!this.$route.query) {
        return [];
      }

      const query = this.$route.query;

      return Object.keys(query)
        .filter(
          (q) =>
            q !== "query" &&
            q !== "page" &&
            q !== "sort_by" &&
            q !== "sort_order"
        )
        // Singular -> just a number, multiple jx[]=a, jx[]=b -> array so we need to flatten it at the same time
        .flatMap((q) =>
          Array.isArray(query[q]) ? query[q].map(q2 => Number(q2)) : Number(query[q])
        )
        // In case there'd ever be a non-number query value we don't wanna get that in the selection - mostly an extra safeguard :)
        .filter(q => !isNaN(q));
    },
    resetFilter() {
      this.$refs.filter.setCheckedKeys([]);

      // Collapse all nodes
      for (let i = 0; i < this.$refs.filter.store._getAllNodes().length; i++) {
        this.$refs.filter.store._getAllNodes()[i].expanded = false;
      }

      this.checkedKeys = [];
      this.customFilterData = null;
      this.updateVisibleFilterData();

      this.$emit("filterReset", {
        queryParams: null,
        licenses: null,
        products: null,
        categories: null,
      });
      if (this.$nuxt.$route.name !== "search") {
        this.$emit("filterChanged", {
          queryParams: null,
          licenses: null,
          products: null,
          categories: null,
          jurisdictionId: null,
        });
      }
    },

    saveFilter() {
      this.$store.commit("Dialog/setData", {
        label: "filterSaverDialogData",
        data: { draw: this.customFilterData, save: this.saveFilterData },
      });
      this.$store.commit("Dialog/setData", {
        label: "filterSaverDialog",
        data: true,
      });
    },

    checkFilterData() {
      let check = false;

      let filtered = "";

      // eslint-disable-next-line no-unused-vars
      function replaceAll(str, term, replacement) {
        return str.replace(new RegExp(escapeRegExp(term), "g"), replacement);
      }

      if (this.customFilterData && this.customFilterData.length > 0) {
        filtered = this.customFilterData
          .replaceAll("filter[classification][]=", "")
          .replaceAll("Indicative", "")
          .replaceAll("Actionable", "")
          .replaceAll("Informative", "")
          .replaceAll("&", "");
      }

      if (filtered) {
        for (const key in filtered) {
          if (filtered[key] && filtered[key].length) {
            check = true;
          }
        }
      }
      return check;
    },
    handleChange() {
      function filterChange() {
        this.updateVisibleFilterData();
        const queryStore = this.$store.state.Search.searchFiltersQuery;

        const query = {};

        if (queryStore !== null) {
          Object.keys(queryStore)
            .filter(
              (q) =>
                q !== "query" &&
                q !== "page" &&
                q !== "sort_by" &&
                q !== "sort_order"
            )
            .forEach((item) => (query[item] = []));
        }

        const checkedNodes = this.$refs.filter
          .getCheckedNodes()
          .filter((el) => el.category !== el.id);

        if (this.$matomo && checkedNodes && checkedNodes.length) {
          let selected = "";
          checkedNodes.forEach((el) => {
            selected += el.category + " - " + el.label + ", ";
          });
          this.$matomo.trackEvent("Search", "Filtering", selected);
        }

        // Same condition but we don't provide the data to churnzero atm
        if (this.$churnZero && checkedNodes && checkedNodes.length) {
          this.$churnZero.trackEvent("Search", "Filtering");
        }

        // Create a new query object
        const filterArr = [];
        this.queryParams = "";
        this.licenseParams = "";
        this.productParams = "";
        this.requirementParams = "";
        if (checkedNodes && checkedNodes.length) {
          checkedNodes.forEach((item) => {
            filterArr.push(item.id);
            const name = item.category;
            const val = item.id;
            const label = item.label;
            if (val && typeof val === "string" && val.startsWith("jur-")) {
              return;
            }

            // TODO need to extend this to handle other category types beyond jurisdictions later on
            this.queryParams += `filter[${name}][]=${val}&`;

            if (name === "licenses") {
              this.licenseParams += `filter[${name}][]=${val}&`;
            }

            if (name === "requirement_categories") {
              this.requirementParams += `filter[${name}][]=${val}&`;
            }

            if (name === "products_and_services") {
              this.productParams += `filter[${name}][]=${val}&`;
            }

            if (query[`${name}[]`] === undefined) {
              query[`${name}[]`] = [];
            }
            query[`${name}[]`].push({ label, val });
          });
        }

        this.$store.commit("Search/setData", {
          label: "searchFiltersQuery",
          data: query,
        });
        this.$store.commit("Search/setSearchQuery", {
          label: "searchQuery",
          query,
        });

        this.customFilterData = this.queryParams;
        this.saveFilterData = filterArr.join();

        this.$emit("filterChanged", {
          queryParams: this.queryParams,
          licenses: this.licenseParams,
          products: this.productParams,
          categories: this.requirementParams,
          setFilterBlockCheckbox: false,
          jurisdictionId: null,
        });
      }

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        filterChange.call(this);
      }, 100);
    },
  },
};
</script>

<style lang="scss">
@import "../../assets/styles/helpers/variables";
@import "../../assets/styles/helpers/mixin";

.filter {
  &-content {
    border-right: 1px solid $border;
  }

  &__title {
    background-color: $lighterBrightBlue;
  }

  .el-collapse {
    border: none;
  }

  .el-tree {
    &-node {
      &__content {
        height: auto;
        align-items: flex-start;
        padding: 1rem 3rem 1rem 0;
        position: relative;
        font-size: 1.3rem;
        line-height: 1.5rem;
        border-bottom: 1px solid $border;
        background-color: $lighterBrightBlue;
        // transition: background-color 0.2s;

        .el-checkbox {
          display: none;
        }
      }

      &__label {
        font-size: 1.2rem;
        padding-left: 1.5rem;
        text-transform: uppercase;
        color: $black;
        font-weight: 600;
        white-space: normal;
      }

      &__expand {
        &-icon {
          position: absolute;
          right: 1.5rem;
          top: 1.2rem;
          display: block;
          width: 1.2rem;
          height: 1.2rem;
          transform: rotate(-90deg);
          margin: 0;

          &::before {
            content: none;
          }

          &::after {
            content: "";
            position: absolute;
            top: 50%;
            left: 50%;
            display: block;
            width: 1.2rem;
            height: 1.2rem;
            margin: -0.6rem 0 0 -0.6rem;
            background: url("../../assets/icons/i_arrow-red.svg") no-repeat
              center;
            background-size: contain;
            transition: 0.3s ease-out;
          }

          &.expanded {
            transform: rotate(90deg);
          }

          &.is-leaf {
            display: none;
          }
        }
      }

      &__children {
        .el-checkbox {
          display: inline-block;
        }

        .is-disabled > .el-checkbox__inner {
          border-color: $border !important;
        }

        .el-tree-node__label {
          padding-left: 0;
          font-size: 1.3rem;
          text-transform: none;
        }

        .is-disabled ~ .el-tree-node__label {
          color: #b1b1b1;
        }

        .el-tree-node__content {
          cursor: default; // All children haven't an arrow
          border-bottom: 0;
          background: none;
        }

        .el-tree-node {
          border-bottom: 1px solid $border;

          .el-tree-node {
            border-bottom: 0;
          }
        }

        .el-tree-node__children {
          .el-tree-node__label {
            font-weight: 400;
          }
        }
      }
    }
  }

  .el-tree-node__content:hover,
  .el-tree-node:focus > .el-tree-node__content {
    background: none;
  }

  &__reset {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem 0;
  }

  &-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    margin-top: 1.5rem;
  }

  &-save {
    background: $red;
    box-shadow: none;
    border-radius: 0;
    border: none;
    font-size: 1.4rem;
    color: $white;
    padding: 0.8rem 1.6rem;
  }

  &-reset {
    background: transparent;
    box-shadow: none;
    border-radius: 0;
    border: none;
    color: $red;
    font-size: 1.4rem;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
